import React, { useState, useEffect } from 'react';
import { 
  Wrench, CheckCircle, Clock, Phone, Calendar, Shield,
  Zap, Sun, Battery, Settings, AlertTriangle, ChevronDown,
  MessageCircle, MapPin, Star, Users, ArrowRight, ClipboardCheck,
  Sparkles, ThumbsUp
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const ServicioTecnicoPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: ClipboardCheck,
      title: 'Mantenimiento Preventivo',
      description: 'Inspección completa del sistema para prevenir fallas y maximizar la producción.',
      includes: [
        'Limpieza de paneles solares',
        'Inspección de conexiones eléctricas',
        'Verificación del inversor',
        'Análisis de producción',
        'Reporte detallado'
      ],
      frequency: 'Anual recomendado',
      price: 'Desde Gs. 350.000'
    },
    {
      icon: Wrench,
      title: 'Reparaciones',
      description: 'Diagnóstico y reparación de fallas en cualquier componente del sistema.',
      includes: [
        'Diagnóstico de fallas',
        'Reparación de cableado',
        'Reemplazo de conectores',
        'Ajuste de estructuras',
        'Reconfiguración de inversor'
      ],
      frequency: 'Según necesidad',
      price: 'Presupuesto previo'
    },
    {
      icon: Sparkles,
      title: 'Optimización',
      description: 'Mejoramos el rendimiento de sistemas existentes para mayor producción.',
      includes: [
        'Análisis de rendimiento',
        'Ajuste de inclinación',
        'Reconfiguración de strings',
        'Actualización de monitoreo',
        'Informe de mejoras'
      ],
      frequency: 'Única vez',
      price: 'Consultar'
    }
  ];

  const plans = [
    {
      name: 'Plan Básico',
      price: 'Gs. 350.000',
      period: '/año',
      features: [
        '1 visita de mantenimiento anual',
        'Limpieza de paneles',
        'Inspección visual',
        'Reporte básico',
        'Descuento 10% en reparaciones'
      ],
      recommended: false
    },
    {
      name: 'Plan Premium',
      price: 'Gs. 600.000',
      period: '/año',
      features: [
        '2 visitas de mantenimiento',
        'Limpieza completa de paneles',
        'Inspección termográfica',
        'Análisis de producción',
        'Reporte detallado',
        '15% descuento en reparaciones',
        'Prioridad en atención'
      ],
      recommended: true
    },
    {
      name: 'Plan Empresarial',
      price: 'Consultar',
      period: '',
      features: [
        'Visitas trimestrales',
        'Monitoreo remoto 24/7',
        'Mantenimiento preventivo completo',
        'Respuesta prioritaria',
        '20% descuento en reparaciones',
        'Técnico dedicado',
        'SLA garantizado'
      ],
      recommended: false
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Contacto',
      description: 'Contactanos por WhatsApp o teléfono describiendo tu necesidad.',
      icon: Phone
    },
    {
      step: 2,
      title: 'Diagnóstico',
      description: 'Evaluamos remotamente o programamos una visita técnica.',
      icon: ClipboardCheck
    },
    {
      step: 3,
      title: 'Presupuesto',
      description: 'Te enviamos un presupuesto detallado antes de cualquier trabajo.',
      icon: Settings
    },
    {
      step: 4,
      title: 'Ejecución',
      description: 'Realizamos el servicio con técnicos certificados.',
      icon: Wrench
    },
    {
      step: 5,
      title: 'Verificación',
      description: 'Comprobamos el correcto funcionamiento y entregamos informe.',
      icon: CheckCircle
    }
  ];

  const emergencyServices = [
    {
      problem: 'Sistema no genera energía',
      urgency: 'Alta',
      response: '24-48 horas'
    },
    {
      problem: 'Error en inversor',
      urgency: 'Alta',
      response: '24-48 horas'
    },
    {
      problem: 'Ruidos o olores extraños',
      urgency: 'Crítica',
      response: 'Mismo día'
    },
    {
      problem: 'Daño visible en paneles',
      urgency: 'Media',
      response: '3-5 días'
    },
    {
      problem: 'Baja producción',
      urgency: 'Baja',
      response: '5-7 días'
    }
  ];

  const faqs = [
    {
      question: '¿Con qué frecuencia debo hacer mantenimiento?',
      answer: 'Recomendamos un mantenimiento preventivo anual como mínimo. En zonas con mucho polvo, hojas o cerca de rutas sin pavimentar, puede necesitarse cada 6 meses. Los sistemas con baterías requieren revisión cada 6 meses.'
    },
    {
      question: '¿Puedo hacer el mantenimiento yo mismo?',
      answer: 'La limpieza básica de paneles con agua y cepillo suave podés hacerla vos mismo. Sin embargo, la inspección eléctrica, verificación del inversor y diagnóstico de rendimiento deben ser realizados por técnicos certificados por seguridad y para mantener las garantías.'
    },
    {
      question: '¿Qué pasa si mi sistema fue instalado por otra empresa?',
      answer: 'Brindamos servicio técnico a cualquier sistema solar, independientemente de quién lo instaló. Realizamos un diagnóstico inicial para conocer el sistema y ofrecerte el mejor servicio.'
    },
    {
      question: '¿El servicio técnico está cubierto por la garantía?',
      answer: 'El mantenimiento preventivo no está cubierto por garantía. Sin embargo, si detectamos una falla cubierta por garantía durante el mantenimiento, la reparación será sin costo. Las visitas por reclamos de garantía son gratuitas.'
    },
    {
      question: '¿Tienen servicio de emergencia?',
      answer: 'Sí, contamos con servicio de emergencia para situaciones críticas como fallas totales del sistema, errores de inversor o problemas de seguridad. El tiempo de respuesta depende de la urgencia del caso.'
    },
    {
      question: '¿Atienden en todo Paraguay?',
      answer: 'Sí, brindamos servicio técnico en todo el país. Para localidades fuera del área metropolitana de Asunción puede aplicar un costo adicional de viático según la distancia.'
    }
  ];

  const stats = [
    { value: '24h', label: 'Respuesta promedio' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="servicio-tecnico-page">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-zinc-900 via-zinc-800 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-2 text-emerald-400 mb-6">
            <Wrench className="w-5 h-5" />
            <span className="text-sm font-medium tracking-wider uppercase">Soporte Profesional</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Servicio Técnico
            <span className="block text-emerald-400">Expertos en Energía Solar</span>
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-2xl mb-8">
            Mantenimiento, reparación y optimización de sistemas solares por técnicos 
            certificados. Atención en todo Paraguay.
          </p>

          <div className="flex flex-wrap gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20necesito%20servicio%20técnico%20para%20mi%20sistema%20solar"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
            >
              <Phone className="w-5 h-5" />
              Solicitar Servicio
            </a>
            <a 
              href="tel:+595991344956"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm"
            >
              <MessageCircle className="w-5 h-5" />
              Emergencias: +595 991 344956
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Nuestros Servicios
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Soluciones completas para mantener tu sistema solar funcionando al máximo rendimiento.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="p-8 bg-zinc-50 rounded-2xl hover:shadow-xl transition-all duration-300 border border-zinc-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-zinc-900">{service.title}</h3>
                      <p className="text-zinc-600 mt-1">{service.description}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm font-medium text-zinc-500 mb-3">Incluye:</p>
                    <ul className="space-y-2">
                      {service.includes.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
                    <div>
                      <span className="text-xs text-zinc-500">Frecuencia: </span>
                      <span className="text-sm font-medium text-zinc-700">{service.frequency}</span>
                    </div>
                    <span className="text-lg font-bold text-emerald-600">{service.price}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Planes de Mantenimiento
            </h2>
            <p className="text-lg text-zinc-600 max-w-2xl mx-auto">
              Mantené tu sistema en óptimas condiciones con nuestros planes de mantenimiento programado.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`p-8 rounded-2xl transition-all ${
                  plan.recommended 
                    ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/30 scale-105' 
                    : 'bg-white border border-zinc-200'
                }`}
              >
                {plan.recommended && (
                  <div className="inline-block px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full mb-4">
                    RECOMENDADO
                  </div>
                )}
                
                <h3 className={`text-xl font-bold mb-2 ${plan.recommended ? 'text-white' : 'text-zinc-900'}`}>
                  {plan.name}
                </h3>
                
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.recommended ? 'text-white' : 'text-emerald-600'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.recommended ? 'text-emerald-200' : 'text-zinc-500'}>
                    {plan.period}
                  </span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.recommended ? 'text-emerald-300' : 'text-emerald-500'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a 
                  href="https://wa.me/595991344956?text=Hola,%20me%20interesa%20el%20plan%20de%20mantenimiento"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full py-3 text-center font-semibold rounded-lg transition-all ${
                    plan.recommended 
                      ? 'bg-white text-emerald-600 hover:bg-emerald-50' 
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  Contratar Plan
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Response */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">
                Tiempos de Respuesta por Urgencia
              </h2>
              <p className="text-zinc-600">
                Priorizamos las emergencias según el tipo de problema
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-amber-200">
                  <th className="text-left py-3 px-4 text-zinc-700 font-semibold">Problema</th>
                  <th className="text-center py-3 px-4 text-zinc-700 font-semibold">Urgencia</th>
                  <th className="text-center py-3 px-4 text-zinc-700 font-semibold">Respuesta</th>
                </tr>
              </thead>
              <tbody>
                {emergencyServices.map((item, index) => (
                  <tr key={index} className="border-b border-amber-100">
                    <td className="py-3 px-4 text-zinc-800">{item.problem}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.urgency === 'Crítica' ? 'bg-red-100 text-red-700' :
                        item.urgency === 'Alta' ? 'bg-amber-100 text-amber-700' :
                        item.urgency === 'Media' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {item.urgency}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-medium text-zinc-700">{item.response}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 text-center">
            <a 
              href="tel:+595991344956"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-all"
            >
              <Phone className="w-5 h-5" />
              Línea de Emergencias
            </a>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              ¿Cómo Funciona?
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="text-center relative">
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-emerald-600/30"></div>
                  )}
                  <div className="relative z-10 w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-zinc-400 text-sm">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-zinc-900 mb-4">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-zinc-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Wrench className="w-16 h-16 mx-auto text-white/80 mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            ¿Tu sistema necesita atención?
          </h2>
          <p className="text-emerald-100 mb-8">
            Contactanos hoy y un técnico especializado te asistirá
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://wa.me/595991344956?text=Hola,%20necesito%20servicio%20técnico"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Solicitar Servicio
            </a>
            <Link 
              to="/soporte/garantias"
              className="px-8 py-4 bg-emerald-800/50 text-white font-semibold rounded-xl hover:bg-emerald-800 transition-all flex items-center gap-2"
            >
              Ver Garantías
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default ServicioTecnicoPage;
